<template>
  <div>
    <v-row>
      <v-col cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.date') }}
        </h6>
        <span class="body-2 green--text">
          {{ timeSlot(date, slotSize) }}
        </span>
      </v-col>

      <v-col cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.status') }}:
        </h6>
        <v-chip :color="statusColors(rawStatus)" small>
          <span class="caption">
            {{ status }}
          </span>
        </v-chip>
      </v-col>

      <v-col cols="12" sm="4" lg="3">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.type') }}
        </h6>
        <span class="body-2">{{ type }}</span>
      </v-col>

      <v-col cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.code') }}
        </h6>
        <span class="body-2">{{ appointment.code }}</span>
      </v-col>

      <v-col>
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.externalCode') }}
        </h6>
        <span class="body-2">
          {{ appointment.external_code ? appointment.external_code : '---' }}
        </span>
      </v-col>

      <v-col v-if="appointment.group_code">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.history.label.groupCode') }}
        </h6>
        <span class="body-2">
          {{ appointment.group_code }}
        </span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="4" lg="2" v-if="realEstate">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.realEstate') }}
        </h6>
        <span class="body-2">
          {{ realEstate.name }}
        </span>
      </v-col>
      <v-col v-if="appointment.total_value" cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.value') }}
        </h6>
        <span class="body-2 font-weight-bold">
          {{ formatCurrency(appointment.total_value) }}
        </span>
      </v-col>
      <v-col v-if="isAdmin && appointment.host_value" cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.hostValue') }}
        </h6>
        <span class="body-2 font-weight-bold">
          {{ formatCurrency(appointment.host_value) }}
        </span>
      </v-col>


      <v-col v-for="data, index in appointment.additional_data" :key="index" cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
            {{ data.title }}
        </h6>
        <span class="body-2">
          {{ data.value ? data.value : '---' }}
        </span>
      </v-col>
    </v-row>

    <v-row v-if="isRentOut">
      <v-col>
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.fci') }}
        </h6>
        <div class="d-flex align-center">
          <span class="body-2 mr-3">
            {{ formattedFciAmount }}
          </span>
          <v-btn
            v-if="isNotAppointmentDone"
            @click="handleUpdateFci"
            color="primary"
            elevation="0"
            small
          >
            <v-icon left>
              mdi-calculator-variant-outline
            </v-icon>
            <span class="caption">
              {{ updateFci }}
            </span>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.branch') }}
        </h6>
        <span class="body-2">
          {{ branchName }}
        </span>
      </v-col>
      <v-col cols="12" sm="4" lg="2">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.workerType') }}
        </h6>
        <span class="body-2">
          {{ workerType }}
        </span>
      </v-col>
    </v-row>

    <ModalUpdateFCI v-if="isRentOut" ref="updateFciModal" />
  </div>
</template>

<script>
import { catalogDate, catalogMonetary, timeSlot } from '@/util/catalog'
import { handleColors } from '@/util/colors'
import { mapActions } from 'vuex'
import { rentOutTypes } from '@/components/appointment/formFieldsCreate/appointmentFormDefaults'

import ModalUpdateFCI from '../modals/ModalUpdateFCI'

export default {
  name: 'AppointmentBasicInfo',

  components: {
    ModalUpdateFCI,
  },

  props: {
    appointment: {
      type: Object,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
      required: false,
    }
  },

  computed: {
    type() {
      return this.$t('appointment.type.' + this.appointment?.type)
    },

    status() {
      return this.$t('appointment.status.' + this.appointment?.status)
    },

    rawStatus() {
      return this.appointment?.status
    },

    isNotAppointmentDone() {
      return this.rawStatus !== 'DONE'
    },

    date() {
      return this.appointment?.date
    },

    formattedDate() {
      if (!this.appointment?.date) return
      return catalogDate(this.appointment?.date)
    },

    realtiesCode() {
      return this.appointment?.realties.map((realty) => realty.code).join(', ')
    },

    realEstate() {
      return this.appointment.real_estate
    },

    updateFci() {
      return this.$t('appointment.label.updateFci')
    },

    fciAmount() {
      return this.appointment?.fci ?? 0
    },

    formattedFciAmount() {
      return this.formatCurrency(this.fciAmount)
    },

    isRentOut() {
      return rentOutTypes.includes(this.appointment.type)
    },

    slotSize() {
      return this.appointment?.slot_size
    },

    branch() {
      return this.appointment?.branch
    },

    branchName() {
      return this.branch?.name ?? '---'
    },

    workerType() {
      return this.appointment?.worker_type ? this.$t(`appointment.workerType.${this.appointment?.worker_type}`) : '---'
    },
  },

  methods: {
    ...mapActions(['openModal']),

    handleUpdateFci() {
      this.$refs.updateFciModal.resetField()
      this.openModal('UPDATE_FCI')
    }
  },

  data() {
    return {
      timeSlot,
      statusColors: handleColors,
      formatCurrency: catalogMonetary,
    }
  }
}
</script>
